import { inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { ResolveFn, Router } from '@angular/router';
import { AuthData } from '@shared/services/auth';
import { LogsData } from '@shared/services/logs';
import { UsersData } from '@shared/services/users';
import { AppStore } from '@stores';
import { catchError, filter, from, map, Observable, of, switchMap, take } from 'rxjs';

export const userDataResolver: ResolveFn<boolean> = (): Observable<boolean> => {
  const appStore = inject(AppStore);
  const usersData = inject(UsersData);
  const authData = inject(AuthData);
  const logs = inject(LogsData);
  const router = inject(Router);

  // Wait for the language to be initialized in the store before proceeding
  // This ensures that languageResolver has completed its work
  const waitForLanguage$ = toObservable(appStore.select('currentLanguage')).pipe(
    filter((lang) => !!lang),
    take(1)
  );

  return waitForLanguage$.pipe(
    switchMap(() => authData.isAuthenticated()),
    switchMap((isAuthenticated) => {
      if (isAuthenticated) {
        return usersData.fetchUserAccount().pipe(
          catchError((error) => {
            logs.error('Startup', 'Failed to fetch user account', error);
            return authData.logout().pipe(
              switchMap(() => from(router.navigate(['/login']))),
              map(() => null)
            );
          })
        );
      }
      return of(null);
    }),
    map(() => true)
  );
};
